import { getAppEnv, isProd } from "./env";
// getStore() instead of `import { store }` so this module can load while store/index
// is still initializing (see store/storeRef.js). Reading `store` at import time
// threw TDZ in prod minify.
import { getStore } from "@root/store/storeRef";
import { setAppToken, setUserTokenAction } from "@root/store/persistedData";
import axios from "axios";
import toast from "react-hot-toast";
import { showFullScreenLoaderAction } from "../components/BottomSheets/store/actions";
import { hitContentDeeplinkNew } from "../components/Tabs/Content/helpers/hitContentDeeplink";
import { COMMON_DEEPLINK_ACTIONS, USER_AGENT_STRING } from "./constants";
import { hitDeeplinkExecutedV2, hitDeeplinkPerformAction } from "./hitDeeplink";
import { getUserAgent } from "./getUserAgent";

// Re-export so existing `import { getUserAgent } from getAxiosConfig` keep working.
export { getUserAgent } from "./getUserAgent";

// DEFUALT APP_API_VERSION IF NOT PROVIDED IN URL OBJECT
const DEFAULT_API_VERSION = 52;

// STATIC ANDROID AND IOS APP VERSION
const IOS_APP_VERSION = "8.22.1";
const ANDROID_APP_VERSION = "1.4.48.2";

// NEXT_PUBLIC_ENV
const v2BaseUrl = "https://api-v2.classplusapp.com";
const urlObject = {
  PROD: {
    BASE_URL: "https://api.classplusapp.com",
    API_GRAPH_URL: "https://api.classplusapp.com/cams/graph-api",
  },
  QA: {
    BASE_URL: "https://apip-qa.classplusapp.com",
    API_GRAPH_URL: "https://apip-qa.classplusapp.com/cams/graph-api",
  },
  PREPROD: {
    BASE_URL: "https://apip-preprod.classplusapp.com",
    API_GRAPH_URL: "https://apip-preprod.classplusapp.com/cams/graph-api",
  },
  STAGING: {
    BASE_URL: "https://apip-staging.classplusapp.com",
    API_GRAPH_URL: "https://apip-staging.classplusapp.com/cams/graph-api",
  },
};

// REFRESH ACCESS TOKEN API ENDPOINTS
const refreshTokenEndpoints = {
  url: () => "/users/refreshAccessToken",
  method: "POST",
};

// FUNCTION TO GET API BASE URL AND API VERSION
const getApiBaseURL = () => {
  return urlObject[getAppEnv()]?.BASE_URL;
};

export const getApiGraphUrl = () => {
  return urlObject[getAppEnv()]?.API_GRAPH_URL;
};

const getApiVersion = () => {
  return (
    urlObject[getAppEnv()]?.API_VERSION ||
    DEFAULT_API_VERSION
  );
};

// FETCHING USER TOKEN FROM URL
export const getUserToken = () => {
  // FETCHING ALL USER TOKENS STORED
  const userTokenReducer = getStore()?.getState()?.persistedDataReducer?.userToken;
  const userTokenQueryParam =
    typeof window !== "undefined"
      ? new URLSearchParams(window?.location?.search).get("token")
      : "";
  const userTokenLocalStorage =
    typeof localStorage !== "undefined"
      ? localStorage.getItem("userToken")
      : "";

  const userTokenRefreshed =
    typeof window !== "undefined" ? window.refreshedToken : "";

  // console.log("V___________5 reducer: ", userTokenReducer);
  // console.log("V___________6 queryParam: ", userTokenQueryParam);
  // console.log("V___________7 localstorage: ", userTokenLocalStorage);
  // console.log("V___________7.5 selected: ", userTokenReducer || userTokenQueryParam || userTokenLocalStorage);

  // ASSIGNING USER TOKEN BASED ON PRIORITY
  const userToken =
    userTokenRefreshed ||
    userTokenReducer ||
    userTokenQueryParam ||
    userTokenLocalStorage;

  // RETURNING USER TOKEN
  return userToken;
};

export const getIsApkQueryParam = () => {
  if (typeof window === "undefined") return "0";
  return new URLSearchParams(window.location.search).get("is_apk") || "0";
};
// keep name for any residual imports (evaluated lazily via Proxy-style getter on access would be ideal;
// export a getter-like constant via function for safe usage in getHeaders)
export const isAPKQueryParam = typeof window !== "undefined"
  ? new URLSearchParams(window.location.search).get("is_apk")
  : "0";

export const getRegion = () => {
  const countryCodeFromReducer =
    getStore()?.getState()?.persistedDataReducer?.countryCode;
  const countryCodeParam =
    typeof window !== "undefined"
      ? new URLSearchParams(window?.location?.search).get("region")
      : "";
  const countryCodeLocalStorage =
    typeof localStorage !== "undefined" ? localStorage.getItem("region") : "";

  // RETURNING USER TOKEN BASED ON PRIORITY
  const countryCode =
    countryCodeParam ||
    countryCodeParam ||
    countryCodeLocalStorage ||
    countryCodeFromReducer ||
    "IN";

  return countryCode;
};

// FUNCTION TO GET APP VERSION
export const getAppVersion = () => {
  if (typeof window === "undefined") {
    return ANDROID_APP_VERSION;
  }
  return /Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(
    typeof window !== "undefined" && window?.navigator.userAgent
  )
    ? ANDROID_APP_VERSION
    : IOS_APP_VERSION;
};

// FUNCTION TO GET DEVICE TYPE FROM NAVIGATOR — see getUserAgent.js

// FUNCTION TO GET MOBILE-AGENT (aditional header for device specific checks)
export const getMobileAgent = (url) => {
  // IF URL IS NOT OF STORE (i.e. doesn't contains /v2/course)
  if (!url || (!url.includes("v2/course") && !url.includes("v3/course")))
  {
    if(!url.includes("mm/v3")) {
      return {}
    }
  }

  // IF SERVER SIDE
  if (typeof window === "undefined") return {};

  // HAVING DEVICE TYPE IN REDUX STORE
  if (getStore()?.getState()?.persistedDataReducer?.deviceType) {
    return {
      "mobile-agent": getStore()?.getState()?.persistedDataReducer?.deviceType,
    };
  }

  // GETTING DEVICE TYPE FROM NAVIGATOR
  if (getUserAgent()) {
    return {
      "mobile-agent": getUserAgent(),
    };
  }

  // ELSE RETURN EMPTY OBJECT (unknown case)
  return {};
};

// FUNCTION TO GET ACCEPT-LANGUAGE
export const getDefaultLanguage = () => {
  // FETCHING ALL DEFAULT LANG STORED
  const defaultLanguageReducer =
    getStore()?.getState()?.persistedDataReducer?.defaultLanguage;
  const defaultLanguageQueryParam =
    typeof window !== "undefined"
      ? new URLSearchParams(window?.location?.search).get("defaultLanguage")
      : "";
  const defaultLanguageLocalStorage =
    typeof localStorage !== "undefined"
      ? localStorage.getItem("defaultLanguage")
      : "";

  // console.log("V___________5 reducer: ", defaultLanguageReducer);
  // console.log("V___________6 queryParam: ", defaultLanguageQueryParam);
  // console.log("V___________7 localstorage: ", defaultLanguageLocalStorage);
  // console.log("V___________7.5 selected: ", defaultLanguageReducer || defaultLanguageQueryParam || defaultLanguageLocalStorage);

  // ASSIGNING USER TOKEN BASED ON PRIORITY
  const defaultLanguage =
    defaultLanguageReducer ||
    defaultLanguageQueryParam ||
    defaultLanguageLocalStorage;

  // RETURNING USER TOKEN
  return defaultLanguage;
};

// FUNCTION TO GET API HEADERS
export const getHeaders = (customToken, region, defaultLanguage) => {
  const token = customToken || getUserToken() || "";
  const headers = {
    "x-access-token": token,
    "Api-Version": String(getApiVersion()),
    "is-apk": getIsApkQueryParam(),
    "accept-language":
      defaultLanguage?.toLowerCase() || getDefaultLanguage()?.toLowerCase() || "en",
    region: region || getRegion() || "IN",
  };
  if (typeof window === "undefined") {
    headers["User-Agent"] = USER_AGENT_STRING.ANDROID;
  }
  // Drop empty header values that some axios versions stringify poorly
  Object.keys(headers).forEach((k) => {
    if (headers[k] === undefined || headers[k] === null || headers[k] === "") {
      if (k !== "x-access-token") delete headers[k];
    }
  });
  return headers;
};

// To test all api calls
// axios.interceptors.request.use(request => {
//   console.log('Starting Request', JSON.stringify(request, null, 2))
//   return request
// })

// axios.interceptors.response.use(response => {
//   console.log('Response:', JSON.stringify(response, null, 2))
//   return response
// })

// AXIOS INTERCEPTOR RESPONSE TO HANDLE REFRESH TOKEN
axios.interceptors.response.use(
  // RETURNING RESPONSE ON SUCCESS
  (res) => res,
  // HANDLING ERROR
  (err) => {
    // console.log("A______1", !err?.config?.url?.includes("v2/course"));
    // console.log("A______2", err.response);
    // const originalRequest = { ...err.config };

    // RUNNING THE REFRESH TOKEN LOGIC IF ERROR RESPONSE CODE IS 401
    if (
      err?.response?.status === 401 &&
      err?.config?.url?.includes("v2/course/courseTabList")
    ) {
      if (typeof window === "undefined") {
        console.log("server token expirecd");
      } else {
        // SHOWING FULL SCREEN LOADER TO HOLD THE USER ACTIONS
        getStore()?.dispatch(showFullScreenLoaderAction(true));

        // REFRESH TOKEN CALLBACK
        window.onRefreshAccessToken = (dataObj) => {
          try {
            const parsedJSON = JSON.parse(dataObj);
            console.log("parsedJSON", parsedJSON);

            const accessToken = parsedJSON?.accessToken;

            setAppToken(accessToken);
            window.refreshedToken = accessToken;
            window?.handleRefreshCourseData();
            getStore()?.dispatch(showFullScreenLoaderAction(false));
          } catch (err) {
            console.log("error", err);
            window.refreshedToken = "";

            localStorage.clear();
            // EXITING WEBVIEW AND SENDING BACK TO USER TO APP HOME PAGE
            hitDeeplinkExecutedV2(
              JSON.stringify({ screen: `SCREEN_HOME` }),
              JSON.stringify({ screen: `SCREEN_HOME` })
            );
            return;
          }
        };

        // HITTING DEEPLINK TO GET NEW ACCESS TOKEN
        hitDeeplinkPerformAction(
          COMMON_DEEPLINK_ACTIONS.ACTION_GET_REFRESH_TOKEN,
          null,
          JSON.stringify({
            screen: COMMON_DEEPLINK_ACTIONS.ACTION_GET_REFRESH_TOKEN,
          })
        );

        // window.onRefreshAccessToken(
        //   '{"accessToken":"eyJhbGciOiJIUzM4NCIsInR5cCI6IkpXVCJ9.eyJpZCI6MTI5MDI3ODMsIm9yZ0lkIjo5NDI0NywidHlwZSI6MSwibW9iaWxlIjoiOTE5NzExMTUzMDc5IiwibmFtZSI6Ikd1bmphbiAgUHJvZHVjdCBDbGFzc3BsdXMiLCJlbWFpbCI6Imd1bmphbkBjbGFzc3BsdXMuY28iLCJpc0ludGVybmF0aW9uYWwiOjAsImRlZmF1bHRMYW5ndWFnZSI6ImVuIiwiY291bnRyeUNvZGUiOiJJTiIsImNvdW50cnlJU08iOiI5MSIsInRpbWV6b25lIjoiR01UKzU6MzAiLCJpc0RpeSI6ZmFsc2UsImlhdCI6MTY1NjQ4NTQ0MiwiZXhwIjoxNjU3MDkwMjQyfQ.X3af69L1u13IxpPN8Z0Mj8plOUkinFLKmAGSOvbO5ljivAI2Clbyc_iKmciB9_2h"}'
        // );
      }
    }
    // RETURNING THE AXIOS ERROR
    return Promise.reject(err);
  }
);


// One-time auth diagnostic in non-prod (helps debug 401s during SPA migration)
let _authDiagnosed = false;
function diagnoseAuthOnce() {
  if (_authDiagnosed || typeof window === "undefined") return;
  if (isProd()) return;
  _authDiagnosed = true;
  const token = getUserToken();
  console.log("[course-webview auth]", {
    env: getAppEnv(),
    baseURL: getApiBaseURL(),
    graphURL: getApiGraphUrl(),
    hasToken: Boolean(token),
    tokenPrefix: token ? String(token).slice(0, 12) + "…" : null,
    search: window.location.search,
  });
}

// DEFAULT AXIOS CONFIG FOR API CALLS
const getAxiosConfig = (
  method,
  urlCallback,
  payload,
  customToken,
  region,
  defaultLanguage,
  extraHeaders,
  isV2 = false
) => {
  diagnoseAuthOnce();
  return {
    method: method, // API METHOD ex. GET POST PATCH
    url: `${
      isV2 && isProd()
        ? v2BaseUrl
        : getApiBaseURL()
    }${urlCallback(payload)}`, // PASSING WHOLE PAYLOAD OBJECT IN URL CALLBACK (DESTRUCT IN ENDPOINTS)
    ...(method === "GET" ? {} : { data: payload }), // PAYLOAD DATA
    headers: {
      ...extraHeaders,
      ...getHeaders(customToken, region, defaultLanguage),
      ...getMobileAgent(urlCallback(payload)),
    }, // API HEADERS // CUSTOM TOKEN IF DON'T WANT TO USE USER TOKEN
  };
};

export default getAxiosConfig;
